import React, { useState } from 'react';
import UnauthorizedDisclosureForm from './UnauthorizedDisclosureForm';
import TransmissionErrorForm from './TransmissionErrorForm';
import ImproperAccessForm from './ImproperAccessForm';
import LostRecordsForm from './LostRecordsForm';
import VendorBreachForm from './VendorBreachForm';
import PatientRightsForm from './PatientRightsForm';

const HipaaForm = ({ setStep, onSubmit, formType }) => {
  // Route to the appropriate form based on formType
  switch (formType) {
    case 'unauthorizedDisclosureForm':
      return <UnauthorizedDisclosureForm setStep={setStep} onSubmit={onSubmit} />;
    case 'transmissionErrorForm':
      return <TransmissionErrorForm setStep={setStep} onSubmit={onSubmit} />;
    case 'improperAccessForm':
      return <ImproperAccessForm setStep={setStep} onSubmit={onSubmit} />;
    case 'lostRecordsForm':
      return <LostRecordsForm setStep={setStep} onSubmit={onSubmit} />;
    case 'vendorBreachForm':
      return <VendorBreachForm setStep={setStep} onSubmit={onSubmit} />;
    case 'patientRightsForm':
      return <PatientRightsForm setStep={setStep} onSubmit={onSubmit} />;
    default:
      // For now, show a placeholder for forms not yet created
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Form Under Development</h2>
          <p>The {formType} form is being developed.</p>
          <button onClick={() => setStep('hipaaMenu')}>Back to Menu</button>
        </div>
      );
  }
};

// Keep the original comprehensive form as a fallback (currently unused)
// eslint-disable-next-line no-unused-vars
const ComprehensiveHipaaForm = ({ setStep, onSubmit, formType }) => {
  const [formData, setFormData] = useState({
    incidentType: formType || '',
    dateTimeDiscovered: '',
    dateTimeOccurred: '',
    discoveredBy: '',
    patientsAffected: '',
    patientNames: '',
    phiTypes: [],
    disclosureMethod: '',
    recipientInfo: '',
    incidentDescription: '',
    causeOfIncident: '',
    immediateActions: '',
    notificationsMade: [],
    patientNotified: false,
    patientNotificationDate: '',
    patientNotificationMethod: '',
    ocrReportRequired: false,
    ocrReportDate: '',
    riskAssessment: '',
    preventiveMeasures: '',
    followUpRequired: false,
    followUpDetails: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
    reportedByPhone: ''
  });

  const getFormTitle = () => {
    const titles = {
      'unauthorizedDisclosureForm': 'Unauthorized Disclosure Report',
      'improperAccessForm': 'Improper Access Report',
      'lostRecordsForm': 'Lost/Misplaced Records Report',
      'vendorBreachForm': 'Vendor/BAA Breach Report',
      'patientRequestForm': 'Patient Rights Violation Report',
      'transmissionErrorForm': 'Transmission Error Report'
    };
    return titles[formType] || 'HIPAA Incident Report';
  };

  const phiTypeOptions = [
    'Patient Names',
    'Social Security Numbers',
    'Medical Record Numbers',
    'Health Insurance Information',
    'Medical History',
    'Treatment Information',
    'Prescription Information',
    'Lab Results',
    'Imaging Results',
    'Mental Health Information',
    'Billing Information',
    'Contact Information',
    'Emergency Contacts',
    'Insurance Claims',
    'Other PHI'
  ];

  const notificationOptions = [
    'Practice Management',
    'Privacy Officer',
    'Legal Counsel',
    'Insurance Provider',
    'Patients Affected',
    'HHS/OCR',
    'State Authorities',
    'Business Associates',
    'Media (if required)'
  ];

  const disclosureMethodOptions = [
    'Verbal Communication',
    'Email',
    'Fax',
    'Mail',
    'Phone',
    'Text Message',
    'Physical Documents',
    'Electronic System',
    'Inadvertent Display',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      formType: 'HIPAADisclosure',
      subType: formType,
      submittedAt: new Date()
    });
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>{getFormTitle()}</h1>
        <p>Complete all relevant fields for this HIPAA incident</p>
      </div>

      <form onSubmit={handleSubmit} className="incident-form">
        {/* Basic Incident Information */}
        <div className="form-section">
          <h3>Incident Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date/Time Discovered *</label>
              <input
                type="datetime-local"
                name="dateTimeDiscovered"
                value={formData.dateTimeDiscovered}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Date/Time Occurred (if different)</label>
              <input
                type="datetime-local"
                name="dateTimeOccurred"
                value={formData.dateTimeOccurred}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Discovered By *</label>
              <input
                type="text"
                name="discoveredBy"
                value={formData.discoveredBy}
                onChange={handleInputChange}
                placeholder="Name and title of person who discovered the incident"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Number of Patients Affected *</label>
              <input
                type="number"
                name="patientsAffected"
                value={formData.patientsAffected}
                onChange={handleInputChange}
                placeholder="Enter number of patients"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Patient Names (if known)</label>
            <textarea
              name="patientNames"
              value={formData.patientNames}
              onChange={handleInputChange}
              placeholder="List patient names if known and applicable"
              rows="3"
            />
          </div>
        </div>

        {/* PHI Information */}
        <div className="form-section">
          <h3>Protected Health Information (PHI) Involved</h3>
          <div className="checkbox-grid">
            {phiTypeOptions.map(option => (
              <label key={option} className="checkbox-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={formData.phiTypes.includes(option)}
                  onChange={(e) => handleMultiSelectChange(e, 'phiTypes')}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        {/* Disclosure Details */}
        <div className="form-section">
          <h3>Disclosure Details</h3>
          
          <div className="form-group">
            <label>Method of Disclosure *</label>
            <select
              name="disclosureMethod"
              value={formData.disclosureMethod}
              onChange={handleInputChange}
              required
            >
              <option value="">Select method...</option>
              {disclosureMethodOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Recipient Information</label>
            <textarea
              name="recipientInfo"
              value={formData.recipientInfo}
              onChange={handleInputChange}
              placeholder="Who received the PHI? Include names, organizations, contact information if known"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Detailed Description of Incident *</label>
            <textarea
              name="incidentDescription"
              value={formData.incidentDescription}
              onChange={handleInputChange}
              placeholder="Provide a detailed description of what happened, including circumstances and timeline"
              rows="5"
              required
            />
          </div>

          <div className="form-group">
            <label>Cause of Incident</label>
            <textarea
              name="causeOfIncident"
              value={formData.causeOfIncident}
              onChange={handleInputChange}
              placeholder="What caused this incident to occur? (human error, system failure, process breakdown, etc.)"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Immediate Actions Taken</label>
            <textarea
              name="immediateActions"
              value={formData.immediateActions}
              onChange={handleInputChange}
              placeholder="Describe any immediate steps taken to address the incident"
              rows="3"
            />
          </div>
        </div>

        {/* Notifications */}
        <div className="form-section">
          <h3>Notifications</h3>
          
          <div className="form-group">
            <label>Notifications Made</label>
            <div className="checkbox-grid">
              {notificationOptions.map(option => (
                <label key={option} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={option}
                    checked={formData.notificationsMade.includes(option)}
                    onChange={(e) => handleMultiSelectChange(e, 'notificationsMade')}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="patientNotified"
                  checked={formData.patientNotified}
                  onChange={handleInputChange}
                />
                Patient(s) Notified
              </label>
            </div>
          </div>

          {formData.patientNotified && (
            <div className="form-row">
              <div className="form-group">
                <label>Patient Notification Date</label>
                <input
                  type="date"
                  name="patientNotificationDate"
                  value={formData.patientNotificationDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Notification Method</label>
                <input
                  type="text"
                  name="patientNotificationMethod"
                  value={formData.patientNotificationMethod}
                  onChange={handleInputChange}
                  placeholder="How were patients notified?"
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="ocrReportRequired"
                  checked={formData.ocrReportRequired}
                  onChange={handleInputChange}
                />
                OCR Report Required (500+ individuals)
              </label>
            </div>
          </div>

          {formData.ocrReportRequired && (
            <div className="form-group">
              <label>OCR Report Date</label>
              <input
                type="date"
                name="ocrReportDate"
                value={formData.ocrReportDate}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>

        {/* Risk Assessment and Prevention */}
        <div className="form-section">
          <h3>Risk Assessment and Prevention</h3>
          
          <div className="form-group">
            <label>Risk Assessment</label>
            <textarea
              name="riskAssessment"
              value={formData.riskAssessment}
              onChange={handleInputChange}
              placeholder="Assess the risk of harm to patients from this disclosure"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Preventive Measures</label>
            <textarea
              name="preventiveMeasures"
              value={formData.preventiveMeasures}
              onChange={handleInputChange}
              placeholder="Describe measures to prevent similar incidents in the future"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="followUpRequired"
                checked={formData.followUpRequired}
                onChange={handleInputChange}
              />
              Follow-up Required
            </label>
          </div>

          {formData.followUpRequired && (
            <div className="form-group">
              <label>Follow-up Details</label>
              <textarea
                name="followUpDetails"
                value={formData.followUpDetails}
                onChange={handleInputChange}
                placeholder="Describe required follow-up actions and timeline"
                rows="3"
              />
            </div>
          )}
        </div>

        {/* Reporter Information */}
        <div className="form-section">
          <h3>Reporter Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Reported By *</label>
              <input
                type="text"
                name="reportedBy"
                value={formData.reportedBy}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="reportedByTitle"
                value={formData.reportedByTitle}
                onChange={handleInputChange}
                placeholder="Your job title"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="reportedByEmail"
                value={formData.reportedByEmail}
                onChange={handleInputChange}
                placeholder="Your email address"
                required
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="reportedByPhone"
                value={formData.reportedByPhone}
                onChange={handleInputChange}
                placeholder="Your phone number"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" className="submit-button">
            Submit Report
          </button>
        </div>
      </form>
    </div>
  );
};

export default HipaaForm;
